// Single source of truth for the admin SPA login state.
//
// Storage keys (legacy, kept verbatim so existing sessions survive a deploy):
//   guide_user_logged_in : "true" when authenticated
//   guide_username       : currently logged-in username
//   guide_auth_token     : opaque HMAC issued by /auth/login
//   guide_role           : "admin" | "ops"  (new, may be missing on old sessions)
//
// Role gating today is enforced client-side only — see auth.go for why.

export type Role = "admin" | "ops";

const K_LOGGED = "guide_user_logged_in";
const K_USER = "guide_username";
const K_TOKEN = "guide_auth_token";
const K_ROLE = "guide_role";

export function isLoggedIn(): boolean {
  return (
    localStorage.getItem(K_LOGGED) === "true" &&
    !!localStorage.getItem(K_TOKEN)
  );
}

export function getUsername(): string {
  return localStorage.getItem(K_USER) || "";
}

export function getAuthToken(): string {
  return localStorage.getItem(K_TOKEN) || "";
}

// Defaults to "admin" when missing so legacy sessions (signed in before this
// release) keep full access — ops accounts only exist after the new login flow.
export function getRole(): Role {
  const r = localStorage.getItem(K_ROLE);
  return r === "ops" ? "ops" : "admin";
}

export function persistLogin(username: string, role: Role, token: string) {
  localStorage.setItem(K_LOGGED, "true");
  localStorage.setItem(K_USER, username);
  localStorage.setItem(K_TOKEN, token);
  localStorage.setItem(K_ROLE, role);
}

export function replaceAuthToken(token: string) {
  localStorage.setItem(K_TOKEN, token);
}

export function clearLogin() {
  localStorage.removeItem(K_LOGGED);
  localStorage.removeItem(K_USER);
  localStorage.removeItem(K_TOKEN);
  localStorage.removeItem(K_ROLE);
}

// Where to land after login (and where ops should bounce back to when they
// try to open anything else).
export function landingPath(role: Role): string {
  return role === "ops" ? "/rental" : "/dashboard";
}

// Paths an ops user is allowed to visit. Anything outside this allow-list
// triggers a redirect to /rental.
const OPS_ALLOWED_PREFIXES = ["/rental"];

export function isAllowedForRole(role: Role, pathname: string): boolean {
  if (role === "admin") return true;
  return OPS_ALLOWED_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(p + "/"),
  );
}
